import React, { useState, useCallback, useLayoutEffect, useMemo } from "react";
import {
	Box,
	TextField,
	Button,
	Typography,
	Checkbox,
	Tooltip,
} from "@material-ui/core";
import { Edit } from "@material-ui/icons";
import WarningChip from "../../../../Common/Chips/WarningChip";
import { isFunction } from "../../../../../utils/mappersUtils/guards";

export const SegmentHeader = React.memo(function SegmentHeader({
	containerId,
	isOverlay = false,
	isTranslationsPreview,
	selectedLang,
	seg = {},
	translationsData,
	onHeaderBlur,
	onActiveChange,
	onEditTranslations,
	resources,
	touched,
}) {
	const [headerValue, setHeaderValue] = useState("");
	const [headerTouched, setHeaderTouched] = useState(false);

	const segId = useMemo(
		() => parseInt(containerId.split("_")[1], 10),
		[containerId]
	);

	const getHeaderValue = useCallback(() => {
		if (isTranslationsPreview) {
			const segTranslationData = translationsData.find(
				(t) => t.type === "SEGMENT" && t.id === segId
			);
			return (
				segTranslationData?.translations?.[selectedLang]?.item?.value || ""
			);
		}

		return seg.description || "";
	}, [
		isTranslationsPreview,
		selectedLang,
		segId,
		translationsData,
		seg.description,
	]);

	useLayoutEffect(() => {
		setHeaderValue(getHeaderValue());
	}, [getHeaderValue]);

	const handleChange = (e) => {
		setHeaderValue(e.target.value.replace(/^\s+/, ""));
	};

	const handleBlur = () => {
		setHeaderTouched(true);
		// update parent only when user leaves the field
		if (isFunction(onHeaderBlur)) {
			onHeaderBlur(containerId, headerValue);
		}
	};

	const effectiveHeaderTouched = touched || headerTouched;

	const headerError = effectiveHeaderTouched && !headerValue.trim();

	return (
		<div
			className="d-flex justify-content-between w-100"
			style={{ alignItems: "center", gap: "16px" }}
		>
			{isTranslationsPreview ? (
				<>
					<Box display="flex" alignItems="center" flexGrow={1}>
						<span
							id={`segment-description-${containerId}${
								isOverlay ? "-overlay" : ""
							}`}
							style={{
								padding: "6px 8px",
								display: "inline-block",
								whiteSpace: "normal",
								wordWrap: "break-word",
								overflowWrap: "break-word",
								maxWidth: "320px",
							}}
						>
							{headerValue || seg.description}
						</span>
						{!headerValue && seg.description && (
							<Tooltip title={resources["TranslationMissing"]}>
								<WarningChip label={resources["TranslationMissing"]} />
							</Tooltip>
						)}
					</Box>

					<Box style={{ flexShrink: 0, marginRight: 12 }}>
						<Button
							variant="outlined"
							color="primary"
							onClick={() => {
								if (isFunction(onEditTranslations)) {
									onEditTranslations(containerId);
								}
							}}
							startIcon={<Edit />}
						>
							<Typography variant="body2" style={{ marginLeft: 4 }}>
								{resources["EditTranslations"]}
							</Typography>
						</Button>
					</Box>
				</>
			) : (
				<TextField
					id={`segment-description-${containerId}${
						isOverlay ? "-overlay" : ""
					}`}
					placeholder={resources["SegmentDescription"]}
					value={headerValue}
					onChange={handleChange}
					onBlur={handleBlur}
					style={{ flexGrow: 1 }}
					inputProps={{ maxLength: 64 }}
					required
					error={headerError}
					helperText={headerError ? resources["FieldRequired"] : ""}
				/>
			)}

			{!isTranslationsPreview && (
				<Tooltip title={resources["SegmentIsActiveTooltip"]}>
					<Checkbox
						id={`segment-active-${containerId}${isOverlay ? "-overlay" : ""}`}
						checked={seg.isActive ?? true}
						onChange={(e) => {
							if (isFunction(onActiveChange)) {
								onActiveChange(containerId, e.target.checked);
							}
						}}
						style={{ padding: "6px" }}
					/>
				</Tooltip>
			)}
		</div>
	);
});
